import { useMemo, useState } from 'react';
import { useActor } from '@xstate/react';
import { useLocation } from 'react-router-dom';
import { Input, RadioGroup } from '@crazyegginc/hatch';

import { useDebounce } from '/src/hooks';
import { TriggerElementSelector } from './TriggerElementSelector';
import { normalizeUrl, generateWildcardUrl } from '/src/utils/url';
import { GOAL_TRACKING_OPTIONS, ELEMENT_SELECTOR_TYPES } from '/src/features/goals/constants';
import { GOAL_ACTIONS } from '/src/features/goals/machines/wizard';

export function ClickTrigger({ service, triggerIndex }) {
  const [state] = useActor(service);
  const location = useLocation();
  const isEditing = location.pathname.includes('edit');
  const { send } = service;

  const { selectedSite } = state.context;

  const values = state.context.payload.triggers[triggerIndex];

  const [touched, setTouched] = useState({ targetUrl: isEditing && !!values.targetUrl });
  const debouncedTargetUrl = useDebounce(values.targetUrl, 500);
  const debouncedWildcardUrl = useDebounce(values.wildcardUrl, 500);
  const errors = state.context.errors?.triggers?.[triggerIndex];

  const debouncedTargetUrlError = useDebounce(touched.targetUrl ? errors?.targetUrl : null, 500, [debouncedTargetUrl]);
  const debouncedWildcardUrlError = useDebounce(touched.wildcardUrl ? errors?.wildcardUrl : null, 500, [
    debouncedWildcardUrl,
  ]);

  const suggestedWildcard = useMemo(() => {
    return generateWildcardUrl(debouncedTargetUrl, selectedSite?.name);
  }, [debouncedTargetUrl, selectedSite?.name]);

  return (
    <>
      <h2 className="text-header-2 mb-5 mt-10">Your button/link is on this page:</h2>

      <div>
        <Input
          placeholder="Search URL"
          value={values.targetUrl}
          onChange={(e) => {
            setTouched((x) => ({ ...x, targetUrl: true }));
            send({
              type: GOAL_ACTIONS.SET_TRIGGER,
              index: triggerIndex,
              key: 'targetUrl',
              value: normalizeUrl(e.target.value.trim(), { appendSlash: false }),
            });
          }}
          error={touched.targetUrl ? debouncedTargetUrlError : null}
        />
        <div className="text-body-4 mt-2.5">
          Example: You can track if someone has clicked on a button to sign up on your homepage: yoursite.com
        </div>
      </div>

      <div className="mb-10 mt-5">
        <RadioGroup
          options={[
            { value: GOAL_TRACKING_OPTIONS.SPECIFIC_PAGE, label: 'Track this button/link only on this page.' },
            {
              value: GOAL_TRACKING_OPTIONS.ANY_PAGE,
              label: 'Include this button/link from any page across my website.',
            },
            {
              value: GOAL_TRACKING_OPTIONS.WILDCARD,
              label: 'Include this button/link for any pages that match a wildcard.',
            },
          ]}
          value={values.trackingOption}
          onChange={(value) => {
            send({ type: GOAL_ACTIONS.SET_TRIGGER, index: triggerIndex, key: 'trackingOption', value });
            if (value !== GOAL_TRACKING_OPTIONS.WILDCARD && values.wildcardUrl) {
              send({ type: GOAL_ACTIONS.SET_TRIGGER, index: triggerIndex, key: 'wildcardUrl', value: undefined });
            }
          }}
        />
        {values.trackingOption === GOAL_TRACKING_OPTIONS.WILDCARD ? (
          <Input
            className="mt-2.5"
            placeholder={`Enter a wildcard pattern: ${suggestedWildcard}`}
            value={values.wildcardUrl}
            error={touched.wildcardUrl ? debouncedWildcardUrlError : null}
            onChange={(e) => {
              setTouched((x) => ({ ...x, wildcardUrl: true }));
              send({ type: GOAL_ACTIONS.SET_TRIGGER, index: triggerIndex, key: 'wildcardUrl', value: e.target.value });
            }}
          />
        ) : null}
      </div>

      {values.trackingOption && debouncedTargetUrl && !debouncedTargetUrlError ? (
        <>
          <h2 className="text-header-2 mb-5 mt-10">Click on the button/link you want to track:</h2>
          <RadioGroup
            options={[
              { value: ELEMENT_SELECTOR_TYPES.SPECIFIC, label: 'Track only the element I click.' },
              // https://app.shortcut.com/crazyegg/story/27554/broad-element-matching-for-goal-triggers
              // { value: 'BROAD', label: 'Track all similar elements' },
              { value: ELEMENT_SELECTOR_TYPES.MANUAL, label: 'Advanced: Track via CSS selector.' },
            ]}
            value={state.context.payload.triggers[triggerIndex].elementSelectorType}
            onChange={(value) =>
              send({ type: GOAL_ACTIONS.SET_TRIGGER, index: triggerIndex, key: 'elementSelectorType', value })
            }
          />

          <div className="text-body-4 mb-2.5 mt-2.5">
            The element you select will be outlined in red. To cancel a selection, click on the element again.
          </div>

          <TriggerElementSelector
            service={service}
            trigger={values}
            url={debouncedTargetUrl}
            triggerIndex={triggerIndex}
            device={state.context.payload.device}
            isManual={values.elementSelectorType === ELEMENT_SELECTOR_TYPES.MANUAL}
            errors={errors}
          />
        </>
      ) : null}
    </>
  );
}
